import React from "react";
import styled, { css } from "styled-components";
import {
  AutoLayoutAlignEnum,
  AutoLayoutDirectionEnum,
  AutoLayoutResizingEnum,
} from "./AutoLayoutEnums";
import { ITheme } from "../../Themes/ThemeTypes";
import { themeColor } from "../../Themes/ThemeFunctions";
import { ThemeColorEnum } from "../../Themes/ThemeEnums";

interface AutoLayoutProps {
  children: React.ReactNode;

  $direction?: AutoLayoutDirectionEnum;

  $align?: AutoLayoutAlignEnum;

  $gap?: number;

  $width?: number;

  $height?: number;

  $horizontalResizing?: AutoLayoutResizingEnum;

  $verticalResizing?: AutoLayoutResizingEnum;

  $backgroundColor?: ThemeColorEnum;

  $leftPadding?: number;

  $rightPadding?: number;

  $topPadding?: number;

  $bottomPadding?: number;

  $padding?: number;

  $topBottomPadding?: number;

  $leftRightPadding?: number;

  $borderRadius?: number;

  theme: ITheme;
}

const AutoLayout = styled.div<AutoLayoutProps>`
  display: flex;
  ${(props) => {
    if (props.$horizontalResizing === AutoLayoutResizingEnum.FIXED && props.$width) {
      return css`
        width: ${props.$width}px;
      `;
    } else if (props.$horizontalResizing === AutoLayoutResizingEnum.FILL) {
      return css`
        width: 100%;
      `;
    } else if (props.$horizontalResizing === AutoLayoutResizingEnum.HUG) {
      return css`
        width: fit-content;
      `;
    }
  }};

  ${(props) => {
    if (props.$verticalResizing === AutoLayoutResizingEnum.FIXED && props.$height) {
      return css`
        height: ${props.$height}px;
      `;
    } else if (props.$verticalResizing === AutoLayoutResizingEnum.FILL) {
      return css`
        height: 100%;
      `;
    } else if (props.$verticalResizing === AutoLayoutResizingEnum.HUG) {
      return css`
        height: fit-content;
      `;
    }
  }};

  ${(props) => {
    if (props.$gap) {
      return css`
        gap: ${props.$gap}px;
      `;
    }
  }};

  ${(props) => {
    if (props.$padding) {
      return css`
        padding: ${props.$padding}px;
      `;
    } else if (props.$topBottomPadding) {
      return css`
        padding: ${props.$topBottomPadding}px 0px;
      `;
    } else if (props.$leftRightPadding) {
      return css`
        padding: 0px ${props.$leftRightPadding}px;
      `;
    }
  }};

  ${(props) => {
    if (props.$leftPadding) {
      return css`
        padding-left: ${props.$leftPadding}px;
      `;
    }
  }};

  ${(props) => {
    if (props.$rightPadding) {
      return css`
        padding-right: ${props.$rightPadding}px;
      `;
    }
  }};

  ${(props) => {
    if (props.$topPadding) {
      return css`
        padding-top: ${props.$topPadding}px;
      `;
    }
  }};

  ${(props) => {
    if (props.$bottomPadding) {
      return css`
        padding-bottom: ${props.$bottomPadding}px;
      `;
    }
  }};

  ${(props) => {
    if (props.$backgroundColor !== undefined) {
      const color = themeColor(props.$backgroundColor, props.theme);
      return css`
        background-color: ${color};
      `;
    }
  }};

  ${(props) => {
    if (props.$borderRadius !== undefined) {
      return css`
        border-radius: ${props.$borderRadius}px;
      `;
    }
  }};

  box-sizing: border-box;

  flex-direction: ${(props) => {
    switch (props.$direction) {
      case AutoLayoutDirectionEnum.HORIZONTAL:
        return "row";
      case AutoLayoutDirectionEnum.VERTICAL:
        return "column";
    }
  }};

  justify-content: ${(props) => {
    if (props.$direction === AutoLayoutDirectionEnum.HORIZONTAL) {
      if (
        props.$align === AutoLayoutAlignEnum.TOP_LEFT ||
        props.$align === AutoLayoutAlignEnum.LEFT ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_LEFT
      ) {
        return "flex-start";
      } else if (
        props.$align === AutoLayoutAlignEnum.TOP_CENTER ||
        props.$align === AutoLayoutAlignEnum.CENTER ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_CENTER
      ) {
        return "center";
      } else if (
        props.$align === AutoLayoutAlignEnum.TOP_RIGHT ||
        props.$align === AutoLayoutAlignEnum.RIGHT ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_RIGHT
      ) {
        return "flex-end";
      }
    } else if (props.$direction === AutoLayoutDirectionEnum.VERTICAL) {
      if (
        props.$align === AutoLayoutAlignEnum.TOP_LEFT ||
        props.$align === AutoLayoutAlignEnum.TOP_CENTER ||
        props.$align === AutoLayoutAlignEnum.TOP_RIGHT
      ) {
        return "flex-start";
      } else if (
        props.$align === AutoLayoutAlignEnum.LEFT ||
        props.$align === AutoLayoutAlignEnum.CENTER ||
        props.$align === AutoLayoutAlignEnum.RIGHT
      ) {
        return "center";
      } else if (
        props.$align === AutoLayoutAlignEnum.BOTTOM_LEFT ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_CENTER ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_RIGHT
      ) {
        return "flex-end";
      }
    }
  }};

  align-items: ${(props) => {
    if (props.$direction === AutoLayoutDirectionEnum.HORIZONTAL) {
      if (
        props.$align === AutoLayoutAlignEnum.TOP_LEFT ||
        props.$align === AutoLayoutAlignEnum.TOP_CENTER ||
        props.$align === AutoLayoutAlignEnum.TOP_RIGHT
      ) {
        return "flex-start";
      } else if (
        props.$align === AutoLayoutAlignEnum.LEFT ||
        props.$align === AutoLayoutAlignEnum.CENTER ||
        props.$align === AutoLayoutAlignEnum.RIGHT
      ) {
        return "center";
      } else if (
        props.$align === AutoLayoutAlignEnum.BOTTOM_LEFT ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_CENTER ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_RIGHT
      ) {
        return "flex-end";
      }
    } else if (props.$direction === AutoLayoutDirectionEnum.VERTICAL) {
      if (
        props.$align === AutoLayoutAlignEnum.TOP_LEFT ||
        props.$align === AutoLayoutAlignEnum.LEFT ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_LEFT
      ) {
        return "flex-start";
      } else if (
        props.$align === AutoLayoutAlignEnum.TOP_CENTER ||
        props.$align === AutoLayoutAlignEnum.CENTER ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_CENTER
      ) {
        return "center";
      } else if (
        props.$align === AutoLayoutAlignEnum.TOP_RIGHT ||
        props.$align === AutoLayoutAlignEnum.RIGHT ||
        props.$align === AutoLayoutAlignEnum.BOTTOM_RIGHT
      ) {
        return "flex-end";
      }
    }
  }};
`;

export default AutoLayout;
